/*
 * Compact mini-cart controller for cart-line-addons.liquid.
 *
 * The main cart's controller lives with its server-rendered component. The
 * mini cart is fetched and injected with innerHTML, so embedded scripts never
 * execute there. Event delegation keeps these controls alive across every
 * drawer refresh while preserving copy-specific `_paired_with_line_ref`
 * relationships for duplicate prints sharing one design OrderID.
 */
(function () {
  if (window.__miniCartLineAddonsLoaded) return;
  window.__miniCartLineAddonsLoaded = true;

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || (data && data.status && data.status >= 400)) {
          var error = new Error(
            (data && (data.description || data.message)) || 'HTTP ' + response.status
          );
          error.payload = data;
          throw error;
        }
        return data;
      });
    });
  }

  function postCart(url, body) {
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function drawerRoot(node) {
    var root = node && node.closest ? node.closest('[data-cln-surface="drawer"]') : null;
    return root && !root.hasAttribute('data-cln-busy') ? root : null;
  }

  function setBusy(root, busy) {
    if (!root) return;
    if (busy) {
      root.setAttribute('data-cln-busy', 'true');
      root.setAttribute('aria-busy', 'true');
    } else {
      root.removeAttribute('data-cln-busy');
      root.removeAttribute('aria-busy');
    }
  }

  function updateCartBadge(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach(function (id) {
      var host = document.getElementById(id);
      if (!host) return;
      var bubble = host.querySelector('.cart-count-bubble');
      if (!bubble) return;
      var visibleCount = bubble.querySelector('[aria-hidden="true"]');
      var accessibleCount = bubble.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = String(count);
      if (accessibleCount) {
        accessibleCount.textContent = count + (count === 1 ? ' item' : ' items');
      }
    });
  }

  function refreshDrawer() {
    var target = document.getElementById('mini-cart');
    if (!target) throw new Error('Mini cart container not found');
    var rootUrl =
      (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    var separator = rootUrl.indexOf('?') === -1 ? '?' : '&';

    return Promise.all([
      fetch(rootUrl + separator + 'section_id=mini-cart'),
      fetchJSON('/cart.js')
    ]).then(function (results) {
      var sectionResponse = results[0];
      var cart = results[1];
      if (!sectionResponse.ok) throw new Error('Failed to refresh mini cart');
      return sectionResponse.text().then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source =
          doc.getElementById('shopify-section-mini-cart') || doc.querySelector('.shopify-section');
        if (!source) throw new Error('Mini cart section missing from response');

        target.innerHTML = source.innerHTML;
        updateCartBadge(cart);
        if (window._gnc_init) window._gnc_init();
        try {
          document.dispatchEvent(
            new CustomEvent('cart:updated', {
              detail: { source: 'mini-cart-line-addons' }
            })
          );
        } catch (_) {}
      });
    });
  }

  function reportFailure(root, error, rollback) {
    console.error('[mini-cart-line-addons] update failed', error);
    setBusy(root, false);
    if (rollback) rollback();
    if (error && error.payload && error.payload.description) {
      window.alert(error.payload.description);
    }
  }

  function giftProductIds(root) {
    return ['data-ribbon-pid', 'data-wrap-pid', 'data-bundle-pid']
      .map(function (k) { return parseInt(root.getAttribute(k), 10) || 0; })
      .filter(Boolean);
  }

  function pairedWraps(cart, root) {
    var lineRef = root.getAttribute('data-line-ref');
    var cachedKey = root.getAttribute('data-paired-key');
    var pids = giftProductIds(root);
    return (cart.items || []).filter(function (line) {
      var properties = line.properties || {};
      return (
        pids.indexOf(line.product_id) !== -1 &&
        (properties._paired_with_line_ref === lineRef ||
          (!properties._paired_with_line_ref && cachedKey && line.key === cachedKey))
      );
    });
  }

  function giftVariantId(root, kind, design) {
    var tier = root.getAttribute('data-size-tier');
    try {
      var id = window.SkylitGifts && window.SkylitGifts.variantFor(kind, tier, design);
      if (id) return id;
    } catch (e) {}
    var attr = { ribbon: 'data-ribbon-id', giftwrap: 'data-wrap-id', bundle: 'data-bundle-id' }[kind];
    return attr ? parseInt(root.getAttribute(attr), 10) || null : null;
  }

  function currentPaper(root) {
    var picker = root.querySelector('[data-gift-paper-picker]');
    return (picker && picker.getAttribute('data-gift-paper-current')) || root.getAttribute('data-paired-design') || 'regular';
  }

  function stampTheme(properties, kind, design) {
    if (kind === 'giftwrap' || kind === 'bundle') {
      var key = window.SkylitGifts ? window.SkylitGifts.designKey(design) : 'regular';
      properties['Gift Wrap Theme'] = window.SkylitGifts ? window.SkylitGifts.labelFor(key) : 'Regular';
      properties._gift_wrap_theme = key;
    }
    return properties;
  }

  function removeLines(lines) {
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return postCart('/cart/change.js', { id: line.key, quantity: 0 });
      });
    }, Promise.resolve());
  }

  function selectWrapPill(root, kind) {
    root.querySelectorAll('[data-cln-wrap]').forEach(function (button) {
      if (button.getAttribute('data-cln-wrap') === kind) {
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.removeAttribute('aria-pressed');
      }
    });
  }

  function setWrapping(root, kind) {
    var previousKind = root.getAttribute('data-paired-kind') || 'none';
    if (kind === previousKind) return;

    var orderId = root.getAttribute('data-order-id');
    var lineRef = root.getAttribute('data-line-ref');
    var paper = currentPaper(root);
    var variantId = kind === 'none' ? null : giftVariantId(root, kind, paper);

    var properties = {
      _paired_with_order_id: orderId,
      _paired_with_line_ref: lineRef
    };
    var activeColour = root.querySelector('[data-cln-color][aria-pressed="true"]');
    var noteInput = root.querySelector('[data-gnc-input]');
    var noteRoot = root.querySelector('.gnc[data-gnc]');
    if (activeColour) properties._ribbon_color = activeColour.getAttribute('data-cln-color');
    if (noteInput && noteInput.value) properties._gift_note = noteInput.value;
    if (noteRoot) {
      if (noteRoot.getAttribute('data-design')) {
        properties._gift_card_design = noteRoot.getAttribute('data-design');
      }
      if (noteRoot.getAttribute('data-font')) {
        properties._gift_card_font = noteRoot.getAttribute('data-font');
      }
    }
    stampTheme(properties, kind, paper);

    selectWrapPill(root, kind);
    setBusy(root, true);
    window.__skylitCartEditInFlight = true;

    fetchJSON('/cart.js')
      .then(function (cart) {
        return removeLines(pairedWraps(cart, root));
      })
      .then(function () {
        if (!variantId) return null;
        return postCart('/cart/add.js', {
          items: [{ id: variantId, quantity: 1, properties: properties }]
        });
      })
      .then(refreshDrawer)
      .catch(function (error) {
        reportFailure(root, error, function () {
          selectWrapPill(root, previousKind);
        });
      })
      .finally(function () {
        window.__skylitCartEditInFlight = false;
      });
  }

  function setColour(root, colour) {
    setBusy(root, true);
    fetchJSON('/cart.js')
      .then(function (cart) {
        var line = pairedWraps(cart, root)[0];
        if (!line) throw new Error('Paired wrapping line not found');
        var properties = Object.assign({}, line.properties || {}, {
          _ribbon_color: colour,
          'Ribbon Colour': colour
        });
        return postCart('/cart/change.js', { id: line.key, properties: properties });
      })
      .then(refreshDrawer)
      .catch(function (error) {
        reportFailure(root, error);
      });
  }

  function setPaper(root, design) {
    var kind = root.getAttribute('data-paired-kind');
    if (kind !== 'giftwrap' && kind !== 'bundle') return;
    if (design === root.getAttribute('data-paired-design')) return;
    var variantId = giftVariantId(root, kind, design);
    if (!variantId) return;
    var orderId = root.getAttribute('data-order-id');
    var lineRef = root.getAttribute('data-line-ref');
    setBusy(root, true);
    window.__skylitCartEditInFlight = true;
    fetchJSON('/cart.js')
      .then(function (cart) {
        var lines = pairedWraps(cart, root);
        var carry = { _paired_with_order_id: orderId, _paired_with_line_ref: lineRef };
        ['_ribbon_color', 'Ribbon Colour', '_gift_note', '_gift_card_design', '_gift_card_font'].forEach(function (k) {
          lines.forEach(function (li) { if (li.properties && li.properties[k]) carry[k] = li.properties[k]; });
        });
        stampTheme(carry, kind, design);
        return removeLines(lines).then(function () {
          return postCart('/cart/add.js', { items: [{ id: variantId, quantity: 1, properties: carry }] });
        });
      })
      .then(refreshDrawer)
      .catch(function (error) { reportFailure(root, error); })
      .finally(function () { window.__skylitCartEditInFlight = false; });
  }

  function setStrips(root, enabled, checkbox) {
    var stripId = parseInt(root.getAttribute('data-wall-strip-id'), 10) || null;
    if (!stripId) return;

    var orderId = root.getAttribute('data-order-id');
    var lineRef = root.getAttribute('data-line-ref');
    var cachedKey = root.getAttribute('data-strip-key');
    setBusy(root, true);

    fetchJSON('/cart.js')
      .then(function (cart) {
        var pairedStrip = (cart.items || []).filter(function (line) {
          var properties = line.properties || {};
          return (
            line.variant_id === stripId &&
            (properties._paired_with_line_ref === lineRef ||
              (!properties._paired_with_line_ref && cachedKey && line.key === cachedKey))
          );
        })[0];

        if (enabled && !pairedStrip) {
          return postCart('/cart/add.js', {
            items: [
              {
                id: stripId,
                quantity: 1,
                properties: {
                  _paired_with_order_id: orderId,
                  _paired_with_line_ref: lineRef
                }
              }
            ]
          });
        }
        if (!enabled && pairedStrip) {
          return postCart('/cart/change.js', { id: pairedStrip.key, quantity: 0 });
        }
        return null;
      })
      .then(refreshDrawer)
      .catch(function (error) {
        reportFailure(root, error, function () {
          if (checkbox) checkbox.checked = !enabled;
        });
      });
  }

  document.addEventListener('click', function (event) {
    var paperButton = event.target.closest && event.target.closest('[data-gift-paper]');
    if (paperButton) {
      var paperRoot = drawerRoot(paperButton);
      if (paperRoot) {
        event.preventDefault();
        setPaper(paperRoot, paperButton.getAttribute('data-gift-paper'));
      }
      return;
    }

    var wrapButton = event.target.closest && event.target.closest('[data-cln-wrap]');
    if (wrapButton) {
      var wrapRoot = drawerRoot(wrapButton);
      if (wrapRoot) {
        event.preventDefault();
        setWrapping(wrapRoot, wrapButton.getAttribute('data-cln-wrap'));
      }
      return;
    }

    var colourButton = event.target.closest && event.target.closest('[data-cln-color]');
    if (colourButton) {
      var colourRoot = drawerRoot(colourButton);
      if (colourRoot) {
        event.preventDefault();
        setColour(colourRoot, colourButton.getAttribute('data-cln-color'));
      }
    }
  });

  document.addEventListener('change', function (event) {
    var checkbox = event.target.closest && event.target.closest('[data-cln-strips]');
    if (!checkbox) return;
    var root = drawerRoot(checkbox);
    if (root) setStrips(root, checkbox.checked, checkbox);
  });

  var initialiseDynamicControls = function () {
    document.querySelectorAll('[data-cln-surface="drawer"]').forEach(function (root) {
      var isFlatPrint = root.getAttribute('data-flat-print') === 'true';
      root.querySelectorAll('[data-print-hidden="true"]').forEach(function (button) {
        button.hidden = isFlatPrint;
      });
    });
    if (window._gnc_init) window._gnc_init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseDynamicControls);
  } else {
    initialiseDynamicControls();
  }

  var miniCart = document.getElementById('mini-cart');
  if (miniCart && window.MutationObserver) {
    new MutationObserver(function () {
      window.requestAnimationFrame(initialiseDynamicControls);
    }).observe(miniCart, { childList: true, subtree: true });
  }
})();
